import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH = 'text-secondary border-b border-secondary px-2 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2 py-1.5 align-middle whitespace-nowrap'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'

// 実行履歴の「種別」。selective は会話を選んでのナレッジ生成（同じ費用台帳に載る）
const TRIGGER_LABEL = { schedule: '定期', manual: '手動', selective: '選択' }

// ナレッジ・回答誘導管理（旧 FAQ・固定回答管理 / メニュー⑩）
export default function AIPurchaseAssistKnowledge() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()

  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [totalPages, setTotalPages] = useState(1)
  const [summary, setSummary] = useState(null)
  const [filterCategory, setFilterCategory] = useState('')
  const [filterEnabled, setFilterEnabled] = useState('')
  const [filterSource, setFilterSource] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null)  // null = 一覧 / 'new' = 新規 / id = 編集
  const [showTester, setShowTester] = useState(false)
  const [tab, setTab] = useState('list')   // list=ナレッジ一覧 / learning=学習

  const fetchSummary = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/knowledge/summary`, { headers: authHeaders() })
      if (r.ok) setSummary(await r.json())
    } catch (e) {/* ignore */}
  }

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
      if (filterCategory) params.set('category', filterCategory)
      if (filterEnabled !== '') params.set('enabled', filterEnabled)
      if (filterSource) params.set('source', filterSource)
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/knowledge?${params}`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setItems(d.items || [])
      setTotal(d.total || 0)
      setTotalPages(d.total_pages || 1)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [siteId, page, filterCategory, filterEnabled, filterSource])
  useEffect(() => { fetchSummary() }, [siteId])

  const remove = async (id, title) => {
    if (!window.confirm(`ナレッジ「${title}」を削除します。よろしいですか？`)) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/knowledge/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    })
    if (r.ok) { fetchAll(); fetchSummary() }
    else alert('削除失敗')
  }

  const toggleEnabled = async (k) => {
    await fetch(`/api/sites/${siteId}/purchase-assist/knowledge/${k.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ enabled: !k.enabled }),
    })
    fetchAll(); fetchSummary()
  }

  const CATEGORY_LABEL = {
    returns: '🔄 返品・交換', shipping: '📦 配送', hours: '🕒 営業時間',
    pricing: '💰 価格・料金', brand: '🏷 ブランド情報', product_info: '🛍 商品情報',
    policy: '📋 ポリシー', custom: '⚙️ カスタム',
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='📚 ナレッジ・回答誘導管理'
        description='頻出質問への正解を固定回答として登録。LLMより優先して即答するため、正確性向上 + コスト削減 + レイテンシ改善が同時に実現できます。'
        rightNode={
          <div className='flex flex-wrap gap-1.5'>
            <Button color={ButtonColor.PRIMARY} onClick={() => setEditing('new')}>+ 新規登録</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => setShowTester(true)}>🧪 マッチテスト</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => { fetchAll(); fetchSummary() }}>🔄 更新</Button>
          </div>
        }
      />

      {/* タブ（一覧 / 学習） */}
      <div className='mb-3 flex gap-1.5'>
        {[['list', '📚 ナレッジ一覧'], ['learning', '🧠 学習']].map(([k, label]) => (
          <button
            key={k} type='button' onClick={() => setTab(k)}
            className={`cursor-pointer rounded-full border px-3 py-1 text-[11px] font-bold ${
              tab === k
                ? 'bg-surface-emphasis text-emphasis border-emphasis'
                : 'bg-surface-secondary text-secondary border-secondary hover:bg-surface-hover'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'learning' && <LearningTab siteId={siteId} authHeaders={authHeaders} onChanged={() => { fetchAll(); fetchSummary() }} />}
      {tab === 'learning' ? null : (
      <>

      {/* サマリー */}
      {summary && (
        <div className='mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2.5'>
          <KpiCard label='登録件数' value={summary.total} sub={`有効: ${summary.enabled} / 無効: ${summary.disabled}`} accent='var(--color-fill-brand)' />
          <KpiCard label='累計ヒット数' value={summary.total_hits.toLocaleString()} sub='LLMコスト削減効果' accent='var(--color-fill-success)' />
          <KpiCard label='カテゴリ数' value={Object.keys(summary.by_category).length} sub={Object.entries(summary.by_category).map(([k, v]) => `${k}:${v}`).slice(0, 3).join(' / ')} accent='var(--color-fill-warning)' />
          <KpiCard label='自動候補' value={summary.by_source?.from_feedback || 0} sub='フィードバックから生成された候補' accent='var(--color-fill-brand)' />
        </div>
      )}

      {/* フィルター */}
      <div className={`${CARD} mb-3 flex flex-wrap gap-1.5 p-2.5`}>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>カテゴリすべて</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterEnabled} onChange={e => { setFilterEnabled(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>状態すべて</option>
          <option value='true'>有効のみ</option>
          <option value='false'>無効のみ</option>
        </select>
        <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>作成元すべて</option>
          <option value='manual'>手動登録</option>
          <option value='from_feedback'>フィードバック由来</option>
        </select>
      </div>

      {error && <div className='bg-surface-critical text-critical mb-2.5 rounded-lg p-2.5 text-xs'>{error}</div>}

      {/* 一覧 */}
      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : items.length === 0 ? (
        <div className={`${CARD} text-secondary p-10 text-center text-xs`}>
          ナレッジが登録されていません。「+ 新規登録」から開始してください。
        </div>
      ) : (
        <>
          <div className='flex flex-col gap-2'>
            {items.map(k => (
              <div
                key={k.id}
                className={`${CARD} p-3 ${k.enabled ? '' : 'opacity-60'}`}
                style={{ borderLeft: `3px solid ${k.enabled ? 'var(--color-fill-brand)' : 'var(--color-fill-disabled)'}` }}
              >
                <div className='flex flex-wrap items-start gap-2'>
                  <div className='min-w-[280px] flex-1'>
                    <div className='mb-1 flex flex-wrap items-center gap-2'>
                      <span className='text-tertiary font-mono text-[9px]'>#{k.id}</span>
                      <span className='bg-surface-emphasis text-emphasis rounded px-2 py-px text-[10px] font-bold'>
                        {CATEGORY_LABEL[k.category] || k.category}
                      </span>
                      <span className='bg-surface-secondary text-secondary rounded px-1.5 py-px text-[10px] font-bold'>
                        {k.match_method === 'exact' ? '完全一致' : k.match_method === 'regex' ? '正規表現' : '部分一致'}
                      </span>
                      {k.source === 'from_feedback' && (
                        <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[10px] font-bold'>FB由来</span>
                      )}
                      {!k.enabled && (
                        <span className='bg-surface-secondary text-secondary rounded px-1.5 py-px text-[10px] font-bold'>無効</span>
                      )}
                      <span className='text-success ml-auto text-[11px]'>
                        ヒット {k.hit_count || 0}回
                      </span>
                    </div>
                    <div className='text-primary mb-1 text-sm font-semibold'>{k.title}</div>
                    {k.question_patterns.length > 0 && (
                      <div className='text-secondary mb-1 text-[11px]'>
                        🔍 質問: {k.question_patterns.slice(0, 3).join(' / ')}
                        {k.question_patterns.length > 3 && <span> +他{k.question_patterns.length - 3}件</span>}
                      </div>
                    )}
                    <div className='bg-surface-secondary text-primary mt-1 rounded p-2 text-xs whitespace-pre-wrap'>
                      💬 {k.answer.length > 200 ? k.answer.slice(0, 200) + '...' : k.answer}
                    </div>
                  </div>
                  <div className='flex flex-col gap-1'>
                    <Button color={ButtonColor.SECONDARY} onClick={() => toggleEnabled(k)}>{k.enabled ? 'OFF' : 'ON'}</Button>
                    <Button color={ButtonColor.SECONDARY} onClick={() => setEditing(k.id)}>編集</Button>
                    <Button color={ButtonColor.CRITICAL} onClick={() => remove(k.id, k.title)}>削除</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className='mt-3 flex items-center justify-center gap-1.5 text-xs'>
              <Button color={ButtonColor.SECONDARY} disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>前へ</Button>
              <span className='text-secondary'>{page} / {totalPages} （計 {total}件）</span>
              <Button color={ButtonColor.SECONDARY} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>次へ</Button>
            </div>
          )}
        </>
      )}
      </>
      )}

      {editing && <KnowledgeEditor siteId={siteId} entryId={editing === 'new' ? null : editing} authHeaders={authHeaders} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchAll(); fetchSummary() }} />}
      {showTester && <KnowledgeTester siteId={siteId} authHeaders={authHeaders} onClose={() => setShowTester(false)} />}
    </Main>
  )
}


// =====================
// 学習タブ（自動学習の設定・承認キュー・実行履歴・失効/劣化の通知）
// =====================
function LearningTab({ siteId, authHeaders, onChanged }) {
  const [settings, setSettings] = useState(null)
  const [queue, setQueue] = useState([])
  const [runs, setRuns] = useState([])
  const [alerts, setAlerts] = useState([])
  const [busy, setBusy] = useState(false)
  // 実行が見送られた・打ち切られた理由。履歴を見に行かせずその場で出す
  const [notice, setNotice] = useState('')

  const load = async () => {
    const get = (p) => fetch(`/api/sites/${siteId}/purchase-assist/${p}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null)
    const [s, q, r, a] = await Promise.all([
      get('learning/settings'), get('knowledge/review-queue'), get('learning/runs'), get('knowledge/lifecycle-alerts'),
    ])
    if (s) setSettings(s)
    if (q) setQueue(q.items || [])
    if (r) setRuns(r.items || [])
    if (a) setAlerts(a.items || [])
  }
  useEffect(() => { load() }, [siteId])

  const saveSettings = async (patch) => {
    setSettings({ ...settings, ...patch })
    await fetch(`/api/sites/${siteId}/purchase-assist/learning/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(patch),
    })
  }

  const post = async (path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    setBusy(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/${path}`, { method: 'POST', headers: authHeaders() })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      await load()
      if (onChanged) onChanged()
      return d
    } catch (e) {
      alert(`失敗: ${e.message}`)
    } finally {
      setBusy(false)
    }
  }

  /** 学習を1回実行する。見送り・打ち切りの理由はその場に出す */
  const runNow = async () => {
    setNotice('')
    const d = await post('learning/run')
    if (!d) return
    if (d.status === 'skipped') setNotice(`実行されませんでした：${d.skipped_reason || '理由が記録されていません'}`)
    else if (d.skipped_reason) setNotice(`途中で打ち切られました：${d.skipped_reason}`)
  }

  if (!settings) return <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>

  const spent = settings.daily_cost_spent_usd || 0
  const limit = settings.daily_cost_limit_usd || 0
  const overLimit = limit > 0 && spent >= limit

  return (
    <div className='flex flex-col gap-3.5'>
      <div className='bg-surface-secondary text-secondary rounded-lg p-2.5 text-[11px]'>
        ここでいう「学習」は<strong>モデルの再学習ではなくナレッジ（固定回答）の蓄積</strong>です。
        蓄積したナレッジは対話時にAIより先に評価され、一致すると<strong>AIを通さずそのまま回答されます</strong>。
        誤ったナレッジはそのまま顧客に出るため、既定では自動生成しても<strong>有効化はせず承認待ち</strong>にしています。
      </div>

      {/* 設定 */}
      <div className={`${CARD} p-3`}>
        <div className='text-emphasis mb-2 text-xs font-bold'>⚙️ 自動学習の設定</div>
        <div className='flex flex-col gap-2.5'>
          <label className='text-primary flex items-center gap-1.5 text-xs'>
            <input type='checkbox' checked={settings.auto_learning_enabled}
                   onChange={e => saveSettings({ auto_learning_enabled: e.target.checked })}
                   className='accent-[var(--color-fill-brand)]' />
            自動学習を有効にする（既定：無効）
          </label>
          <div className='grid grid-cols-2 gap-2.5'>
            <LField label='対象の会話'>
              <select value={settings.target_scope} onChange={e => saveSettings({ target_scope: e.target.value })} className={`${INPUT} w-full`}>
                <option value='purchased_or_thumbs_up'>購買あり または 👍</option>
                <option value='purchased'>購買ありのみ</option>
                <option value='thumbs_up'>👍 のみ</option>
                <option value='all'>すべて</option>
              </select>
            </LField>
            <LField label='最低出現数（ユニーク訪問者数）'>
              <input type='number' min={1} value={settings.min_occurrences}
                     onChange={e => saveSettings({ min_occurrences: parseInt(e.target.value) || 3 })} className={`${INPUT} w-full`} />
            </LField>
            <LField label='対象期間（日）'>
              <input type='number' min={1} value={settings.lookback_days}
                     onChange={e => saveSettings({ lookback_days: parseInt(e.target.value) || 14 })} className={`${INPUT} w-full`} />
            </LField>
            <LField label='1回の生成上限'>
              <input type='number' min={1} value={settings.max_entries_per_run}
                     onChange={e => saveSettings({ max_entries_per_run: parseInt(e.target.value) || 10 })} className={`${INPUT} w-full`} />
            </LField>
            <CostLimitField limit={limit} spent={spent}
                            onSave={v => saveSettings({ daily_cost_limit_usd: v })} />
          </div>

          <label className='text-primary flex items-center gap-1.5 text-xs'>
            <input type='checkbox' checked={settings.auto_approve_enabled}
                   onChange={e => saveSettings({ auto_approve_enabled: e.target.checked })}
                   className='accent-[var(--color-fill-brand)]' />
            確からしさが高いものを自動承認する（既定：無効）
          </label>
          {settings.auto_approve_enabled && (
            <>
              <div className='bg-surface-warning text-warning rounded-lg p-2.5 text-[11px]'>
                ⚠ 自動承認したナレッジは<strong>AIを通さず顧客に出ます</strong>。
                しばらくは無効のまま運用し、人が承認・却下した実績と確からしさの分布を見てから
                閾値を決めることを推奨します。
              </div>
              <div className='grid grid-cols-3 gap-2.5'>
                <LField label='自動承認の閾値（0-100）'>
                  <input type='number' min={0} max={100} value={settings.auto_approve_threshold}
                         onChange={e => saveSettings({ auto_approve_threshold: parseInt(e.target.value) || 85 })} className={`${INPUT} w-full`} />
                </LField>
                <LField label='1回の自動承認上限'>
                  <input type='number' min={1} value={settings.max_auto_approve_per_run}
                         onChange={e => saveSettings({ max_auto_approve_per_run: parseInt(e.target.value) || 3 })} className={`${INPUT} w-full`} />
                </LField>
                <LField label='観察期間（日）'>
                  <input type='number' min={1} value={settings.observation_days}
                         onChange={e => saveSettings({ observation_days: parseInt(e.target.value) || 7 })} className={`${INPUT} w-full`} />
                </LField>
              </div>
              <div className='text-tertiary text-[10px]'>
                観察期間中に成果が下がっても<strong>自動では無効化しません</strong>。通知のみを行い、判断は運営者に委ねます。
              </div>
            </>
          )}

          {notice && (
            <div className='bg-surface-warning text-warning rounded-lg p-2.5 text-[11px]'>{notice}</div>
          )}

          <div className='flex flex-wrap items-center gap-1.5'>
            <Button color={ButtonColor.PRIMARY} disabled={busy || overLimit} onClick={runNow}>▶ いま学習を実行</Button>
            {overLimit && (
              <span className='text-critical text-[10px]'>
                本日の費用上限に到達しています。明日まで実行できません
              </span>
            )}
            <Button color={ButtonColor.SECONDARY} disabled={busy} onClick={() => post('knowledge/sweep')}>🧹 棚卸しを実行</Button>
            <Button color={ButtonColor.CRITICAL} disabled={busy}
                    onClick={() => post('knowledge/kill-switch', '自動生成されたナレッジをすべて無効化します。手動で作成したものは影響を受けません。よろしいですか？')}>
              ⛔ 自動生成分を一括停止
            </Button>
          </div>
        </div>
      </div>

      {/* 承認キュー */}
      <div className={`${CARD} p-3`}>
        <div className='text-emphasis mb-2 text-xs font-bold'>✅ 承認待ち（{queue.length}件）</div>
        {queue.length === 0 ? (
          <div className='text-tertiary py-4 text-center text-xs'>承認待ちの候補はありません。</div>
        ) : queue.map(q => (
          <ReviewCard key={q.id} item={q} siteId={siteId} authHeaders={authHeaders} onDone={() => { load(); if (onChanged) onChanged() }} />
        ))}
      </div>

      {/* 失効・劣化の通知 */}
      {alerts.length > 0 && (
        <div className={`${CARD} p-3`}>
          <div className='text-emphasis mb-2 text-xs font-bold'>🔔 失効・劣化の通知（{alerts.length}件）</div>
          {alerts.map(a => (
            <div key={a.id} className='mb-2 rounded-lg border border-secondary p-2.5'>
              <div className='flex flex-wrap items-center gap-2'>
                <span className={`rounded px-1.5 py-px text-[9px] font-bold ${
                  a.lifecycle_status === 'expired' ? 'bg-surface-critical text-critical'
                    : a.lifecycle_status === 'degraded' ? 'bg-surface-warning text-warning'
                    : 'bg-surface-secondary text-secondary'}`}>
                  {a.lifecycle_status === 'expired' ? '失効（無効化済み）'
                    : a.lifecycle_status === 'degraded' ? '成果低下（有効のまま）' : '休眠'}
                </span>
                <span className='text-primary text-xs font-semibold'>{a.title}</span>
                <span className='text-tertiary text-[10px]'>ヒット {a.hit_count}回</span>
              </div>
              {a.detail?.reason && <div className='text-secondary mt-1 text-[11px]'>{a.detail.reason}</div>}
              {a.detail?.factors && (
                <div className='mt-1.5'>
                  {a.detail.factors.map((f, i) => (
                    <div key={i} className='text-secondary text-[11px]'>
                      <span className='text-tertiary'>[{f.axis}]</span> {f.finding} → {f.implication}
                    </div>
                  ))}
                  <div className='text-emphasis mt-1 text-[11px] font-bold'>推奨: {a.detail.recommendation}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 実行履歴 */}
      <div className={`${CARD} overflow-x-auto p-3`}>
        <div className='text-emphasis mb-2 text-xs font-bold'>🕒 実行履歴</div>
        {runs.length === 0 ? (
          <div className='text-tertiary py-4 text-center text-xs'>まだ実行されていません。</div>
        ) : (
          <table className='w-full border-collapse text-[11px]'>
            <thead>
              <tr className='bg-surface-secondary'>
                <th className={TH}>日時</th><th className={TH}>種別</th><th className={TH}>状態</th>
                <th className={TH}>対象</th><th className={TH}>除外</th><th className={TH}>生成</th>
                <th className={TH}>自動承認</th><th className={TH}>費用</th><th className={TH}>備考</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} className='border-b border-secondary'>
                  <td className={TD}>{r.started_at ? new Date(r.started_at).toLocaleString('ja-JP') : '-'}</td>
                  <td className={TD}>{TRIGGER_LABEL[r.trigger] || '定期'}</td>
                  <td className={TD}>{r.status}</td>
                  <td className={TD}>{r.target_conversations}</td>
                  <td className={TD}>{r.excluded_conversations}</td>
                  <td className={TD}>{r.generated}</td>
                  <td className={TD}>{r.auto_approved}</td>
                  <td className={TD}>${(r.cost_usd || 0).toFixed(4)}</td>
                  <td className={`${TD} text-tertiary`}>{r.skipped_reason || r.error || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

/** 日次コスト上限。範囲外は保存せずその場で指摘する（0 以下だと学習が一切動かない） */
function CostLimitField({ limit, spent, onSave }) {
  const [draft, setDraft] = useState(String(limit ?? 1))
  const [error, setError] = useState('')

  useEffect(() => { setDraft(String(limit ?? 1)); setError('') }, [limit])

  // 入力の途中で保存すると「1」を打った時点で上限 $1 になってしまうため、
  // 確定（フォーカスが外れる／Enter）のタイミングでだけ保存する
  const commit = () => {
    const v = parseFloat(draft)
    if (!isFinite(v) || v <= 0 || v > 100) {
      setError('0 より大きく 100 以下で入力してください')
      return
    }
    setError('')
    if (v !== limit) onSave(v)
  }

  const over = limit > 0 && spent >= limit
  return (
    <LField label='日次コスト上限（USD）'>
      <input type='number' min={0.01} max={100} step={0.1} value={draft}
             onChange={e => setDraft(e.target.value)} onBlur={commit}
             onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
             className={`${INPUT} w-full`} />
      <div className={`mt-1 text-[10px] ${over ? 'text-critical' : 'text-tertiary'}`}>
        本日の使用額 ${spent.toFixed(4)} / 上限 ${(limit || 0).toFixed(2)}
        {over && '（到達）'}
      </div>
      {error && <div className='text-critical mt-1 text-[10px]'>{error}</div>}
    </LField>
  )
}

function LField({ label, children }) {
  return (
    <div>
      <label className='text-secondary mb-1 block text-[11px] font-semibold'>{label}</label>
      {children}
    </div>
  )
}

/** 承認待ち1件。確からしさの内訳と、照合できなかった数値を必ず見せる */
function ReviewCard({ item, siteId, authHeaders, onDone }) {
  const [open, setOpen] = useState(false)
  const b = item.score_breakdown || {}
  const unverified = b.grounding?.unverified || []
  const gates = b.gates || []

  const approve = async () => {
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/knowledge/${item.id}/approve`, {
      method: 'POST', headers: authHeaders(),
    })
    if (r.ok) onDone()
    else alert('承認に失敗しました')
  }
  const reject = async () => {
    if (!window.confirm(`「${item.title}」を削除します。よろしいですか？`)) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/knowledge/${item.id}`, {
      method: 'DELETE', headers: authHeaders(),
    })
    if (r.ok) onDone()
    else alert('削除に失敗しました')
  }

  return (
    <div className='mb-2 rounded-lg border border-secondary p-2.5'>
      <div className='flex flex-wrap items-center gap-2'>
        <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${
          (item.confidence_score ?? 0) >= 85 ? 'bg-surface-success text-success'
            : (item.confidence_score ?? 0) >= 60 ? 'bg-surface-warning text-warning'
            : 'bg-surface-critical text-critical'}`}>
          確からしさ {item.confidence_score ?? '-'}
        </span>
        <span className='text-primary text-xs font-semibold'>{item.title}</span>
        <span className='text-tertiary text-[10px]'>{item.source === 'auto_learned' ? '自動生成' : '選択学習'}</span>
        <div className='ml-auto flex gap-1.5'>
          <Button color={ButtonColor.SECONDARY} onClick={() => setOpen(!open)}>{open ? '閉じる' : '詳細'}</Button>
          <Button color={ButtonColor.PRIMARY} onClick={approve}>承認して有効化</Button>
          <Button color={ButtonColor.CRITICAL} onClick={reject}>却下</Button>
        </div>
      </div>

      {gates.length > 0 && (
        <div className='bg-surface-critical text-critical mt-1.5 rounded p-2 text-[11px]'>
          ⚠ 自動承認できない理由: {gates.join(' / ')}
        </div>
      )}
      {unverified.length > 0 && (
        <div className='bg-surface-warning text-warning mt-1.5 rounded p-2 text-[11px]'>
          根拠を確認できない数値: {unverified.map(u => u.text).join('・')}
          <span className='ml-1'>（元のやり取りや商品マスタに見つかりませんでした）</span>
        </div>
      )}

      {open && (
        <div className='mt-2 text-[11px]'>
          <div className='text-secondary mb-1'>質問パターン: {(item.question_patterns || []).join(' / ')}</div>
          <div className='bg-surface-secondary text-primary mb-2 rounded p-2 whitespace-pre-wrap'>{item.answer}</div>
          <div className='grid grid-cols-5 gap-1.5'>
            {[
              ['再現性', b.recurrence], ['根拠', b.grounding], ['成果', b.outcome],
              ['一貫性', b.consistency], ['明確性', b.clarity],
            ].map(([label, v]) => (
              <div key={label} className='bg-surface-secondary rounded p-1.5'>
                <div className='text-tertiary text-[10px]'>{label}</div>
                <div className='text-primary font-bold'>{v?.score ?? '-'} <span className='text-tertiary font-normal'>/ {v?.max ?? '-'}</span></div>
              </div>
            ))}
          </div>
          {b.recurrence?.unique_visitors != null && (
            <div className='text-tertiary mt-1'>ユニーク訪問者 {b.recurrence.unique_visitors}人</div>
          )}
          {item.notes && <div className='text-tertiary mt-1'>{item.notes}</div>}
        </div>
      )}
    </div>
  )
}


// =====================
// 編集オーバーレイ
// =====================
function KnowledgeEditor({ siteId, entryId, authHeaders, onClose, onSaved }) {
  const isNew = entryId === null
  const [draft, setDraft] = useState({
    title: '',
    category: 'custom',
    question_patterns: [''],
    answer: '',
    match_method: 'partial',
    case_sensitive: false,
    enabled: true,
    priority: 100,
    notes: '',
  })
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isNew) return
    fetch(`/api/sites/${siteId}/purchase-assist/knowledge/${entryId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        setDraft({
          title: d.title,
          category: d.category,
          question_patterns: d.question_patterns.length > 0 ? d.question_patterns : [''],
          answer: d.answer,
          match_method: d.match_method,
          case_sensitive: d.case_sensitive,
          enabled: d.enabled,
          priority: d.priority,
          notes: d.notes || '',
        })
      })
      .finally(() => setLoading(false))
  }, [entryId])

  const save = async () => {
    if (!draft.title.trim()) { alert('タイトルを入力してください'); return }
    if (!draft.answer.trim()) { alert('回答を入力してください'); return }
    const patterns = draft.question_patterns.filter(p => p.trim())
    if (patterns.length === 0) { alert('少なくとも1つの質問パターンを入力してください'); return }
    setSaving(true)
    try {
      const url = isNew
        ? `/api/sites/${siteId}/purchase-assist/knowledge`
        : `/api/sites/${siteId}/purchase-assist/knowledge/${entryId}`
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...draft, question_patterns: patterns }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || `HTTP ${r.status}`)
      }
      onSaved()
    } catch (e) {
      alert(`保存失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const addPattern = () => setDraft({ ...draft, question_patterns: [...draft.question_patterns, ''] })
  const removePattern = (i) => setDraft({ ...draft, question_patterns: draft.question_patterns.filter((_, idx) => idx !== i) })
  const updatePattern = (i, v) => {
    const next = [...draft.question_patterns]
    next[i] = v
    setDraft({ ...draft, question_patterns: next })
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-start justify-center bg-black/60 p-8 pb-20 backdrop-blur-sm'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[90vh] w-full max-w-[720px] overflow-auto p-5`}>
        <div className='mb-3.5 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>
            {isNew ? '📚 新規ナレッジ登録' : `📚 ナレッジ編集 #${entryId}`}
          </h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>

        {loading ? (
          <div className='text-secondary py-8 text-center text-sm'>読み込み中...</div>
        ) : (
          <div className='flex flex-col gap-3'>
            <Field label='タイトル（運用識別用）' required>
              <input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder='例: 返品方法のFAQ' className={`${INPUT} w-full`} />
            </Field>

            <div className='grid grid-cols-2 gap-2.5'>
              <Field label='カテゴリ'>
                <select value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} className={`${INPUT} w-full`}>
                  <option value='returns'>🔄 返品・交換</option>
                  <option value='shipping'>📦 配送</option>
                  <option value='hours'>🕒 営業時間</option>
                  <option value='pricing'>💰 価格・料金</option>
                  <option value='brand'>🏷 ブランド情報</option>
                  <option value='product_info'>🛍 商品情報</option>
                  <option value='policy'>📋 ポリシー</option>
                  <option value='custom'>⚙️ カスタム</option>
                </select>
              </Field>
              <Field label='マッチング方式' hint='exact:完全一致 / partial:部分一致(推奨) / regex:正規表現'>
                <select value={draft.match_method} onChange={e => setDraft({ ...draft, match_method: e.target.value })} className={`${INPUT} w-full`}>
                  <option value='partial'>partial（部分一致）</option>
                  <option value='exact'>exact（完全一致）</option>
                  <option value='regex'>regex（正規表現）</option>
                </select>
              </Field>
            </div>

            <Field label='質問パターン（複数登録で表記ゆれ吸収）' required>
              <div className='flex flex-col gap-1'>
                {draft.question_patterns.map((p, i) => (
                  <div key={i} className='flex gap-1'>
                    <input value={p} onChange={e => updatePattern(i, e.target.value)} placeholder='例: 返品方法を教えて / 返品したい / how to return' className={`${INPUT} flex-1`} />
                    <Button color={ButtonColor.CRITICAL} disabled={draft.question_patterns.length === 1} onClick={() => removePattern(i)}>削除</Button>
                  </div>
                ))}
                <div>
                  <Button color={ButtonColor.SECONDARY} onClick={addPattern}>+ パターン追加</Button>
                </div>
              </div>
            </Field>

            <Field label='固定回答' required hint='LLMより優先してこの内容で即答されます'>
              <textarea
                value={draft.answer}
                onChange={e => setDraft({ ...draft, answer: e.target.value })}
                rows={5}
                placeholder='正確な回答を記入してください'
                className={`${TEXTAREA} leading-relaxed`}
              />
            </Field>

            <div className='grid grid-cols-3 gap-2.5'>
              <Field label='優先度' hint='低いほど先にマッチ'>
                <input type='number' min={1} max={999} value={draft.priority} onChange={e => setDraft({ ...draft, priority: parseInt(e.target.value) || 100 })} className={`${INPUT} w-full`} />
              </Field>
              <Field label='大文字小文字を区別'>
                <label className='text-primary flex items-center gap-1.5 py-1 text-xs'>
                  <input type='checkbox' checked={draft.case_sensitive} onChange={e => setDraft({ ...draft, case_sensitive: e.target.checked })} />
                  区別する
                </label>
              </Field>
              <Field label='状態'>
                <label className='text-primary flex items-center gap-1.5 py-1 text-xs'>
                  <input type='checkbox' checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} />
                  有効
                </label>
              </Field>
            </div>

            <Field label='運用メモ（任意）'>
              <textarea value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} rows={2} placeholder='登録経緯や注意事項' className={TEXTAREA} />
            </Field>

            <div className='mt-2 flex justify-end gap-1.5'>
              <Button color={ButtonColor.SECONDARY} onClick={onClose}>キャンセル</Button>
              <Button color={ButtonColor.PRIMARY} disabled={saving} onClick={save}>{saving ? '保存中...' : '✓ 保存'}</Button>
            </div>
          </div>
        )}
      </div>
    </ModalOverlay>
  )
}


// =====================
// マッチテスター
// =====================
function KnowledgeTester({ siteId, authHeaders, onClose }) {
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  const test = async () => {
    if (!text.trim()) return
    setLoading(true)
    setResult(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/knowledge/test-match?text=${encodeURIComponent(text)}`, { headers: authHeaders() })
      const d = await r.json()
      setResult(d)
    } catch (e) {
      setResult({ error: e.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-start justify-center bg-black/60 p-10 backdrop-blur-sm'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[85vh] w-full max-w-[640px] overflow-auto p-5`}>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>🧪 マッチテスト</h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>
        <p className='text-secondary mt-0 mb-2.5 text-[11px]'>
          ユーザー入力を想定したテキストを入力すると、登録済ナレッジのマッチ結果を確認できます。
        </p>
        <div className='mb-2.5 flex gap-1.5'>
          <input value={text} onChange={e => setText(e.target.value)}
            // 検査は「検査」ボタンのみ（IMEの変換確定 Enter による誤実行を防ぐ）
            placeholder='例: 返品はどうすればいい？' className={`${INPUT} flex-1`} />
          <Button color={ButtonColor.PRIMARY} disabled={loading || !text.trim()} onClick={test}>{loading ? '...' : '🔍 検査'}</Button>
        </div>

        {result && (
          <div>
            {result.error ? (
              <div className='bg-surface-critical text-critical rounded-lg p-2.5 text-xs'>{result.error}</div>
            ) : result.matched.length === 0 ? (
              <div className='bg-surface-secondary text-secondary rounded-lg p-3.5 text-center text-xs'>
                該当するナレッジはありません（このパターンはLLMで応答されます）
              </div>
            ) : (
              <div>
                <div className='bg-surface-success text-success mb-2.5 rounded-lg p-2.5 text-xs'>
                  ✓ {result.matched.length} 件のナレッジがマッチしました（優先度順）
                </div>
                <div className='bg-surface-emphasis mb-2.5 rounded-lg border border-secondary p-2.5'>
                  <div className='text-emphasis mb-1 text-[11px] font-bold'>📤 返される回答（最優先のもの）</div>
                  <div className='text-primary text-[13px] whitespace-pre-wrap'>{result.first_answer}</div>
                </div>
                <div className='flex flex-col gap-1.5'>
                  {result.matched.map(m => (
                    <div key={m.id} className='bg-surface-secondary rounded p-2 text-[11px]'>
                      <span className='text-tertiary'>#{m.id}</span>
                      <span className='bg-surface-emphasis text-emphasis ml-1.5 rounded-sm px-1.5 py-px'>優先度 {m.priority}</span>
                      <span className='text-primary ml-1.5 font-semibold'>{m.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalOverlay>
  )
}


function KpiCard({ label, value, sub, accent }) {
  return (
    <div className={`${CARD} p-3`} style={{ borderLeft: `3px solid ${accent}` }}>
      <div className='text-secondary text-[10px]'>{label}</div>
      <div className='text-primary mt-0.5 text-lg font-bold'>{value}</div>
      {sub && <div className='text-tertiary mt-0.5 text-[9px]'>{sub}</div>}
    </div>
  )
}

function Field({ label, hint, children, required }) {
  return (
    <div className='flex flex-col gap-1'>
      <label className='text-secondary text-[11px] font-medium'>
        {label}{required && <span className='text-critical ml-0.5'>*</span>}
        {hint && <span className='text-tertiary ml-1.5 text-[10px] font-normal'>— {hint}</span>}
      </label>
      {children}
    </div>
  )
}
